import { useEffect, useRef } from 'react';
import gsap from 'gsap';

const CALENDLY_URL = 'https://paystack.com/buy/private-clarity-call-sdonrr';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const eyebrowRef = useRef<HTMLParagraphElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subheadRef = useRef<HTMLParagraphElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const smallTextRef = useRef<HTMLParagraphElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo(
        eyebrowRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.8 }
      )
        .fromTo(
          headlineRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 1 },
          '-=0.4'
        )
        .fromTo(
          subheadRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8 },
          '-=0.5'
        )
        .fromTo(
          bodyRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8 },
          '-=0.4'
        )
        .fromTo(
          buttonsRef.current,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.8 },
          '-=0.4'
        )
        .fromTo(
          smallTextRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.6 },
          '-=0.3'
        )
        .fromTo(
          imageRef.current,
          { opacity: 0, scale: 1.05 },
          { opacity: 1, scale: 1, duration: 1.2 },
          '-=1.5'
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen bg-[#0D0D0D] overflow-hidden"
    >
      {/* Full-bleed background image */}
      <div
        ref={imageRef}
        className="absolute inset-0 opacity-0"
      >
        <img
          src="https://static.readdy.ai/image/2d29664032ce3429a58840abfd6f4538/0b1ff9b16f52001da26ad9ef0664ab0d.png"
          alt="Mandie Ozee, Clinical Psychologist and Transformation Coach"
          className="absolute inset-0 w-full h-full object-cover object-center"
          width="800"
          height="900"
        />
      </div>

      {/* Dark overlay — always visible, not tied to image animation */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/55 to-black/65 z-[2]" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
        {/* Left Content */}
        <div className="w-full lg:w-[45%] flex flex-col justify-center px-6 md:px-10 lg:px-16 py-24 lg:pt-20 lg:pb-0">
          <p
            ref={eyebrowRef}
            className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6 opacity-0"
          >
            Clinical Psychologist · Coach · Healer
          </p>

          <h1
            ref={headlineRef}
            className="font-heading text-xl md:text-4xl lg:text-5xl leading-[1.15] text-[#FDFAF8] mb-6 opacity-0"
          >
            You were never broken.
            <br />
            You were just waiting
            <br />
            to be brought back
            <br />
            to yourself.
          </h1>

          <p
            ref={subheadRef}
            className="font-quote text-base md:text-lg italic text-[#F0B8D0] mb-6 opacity-0"
          >
            This is where the coming home begins.
          </p>

          <p
            ref={bodyRef}
            className="font-body text-sm md:text-base text-[#E8E8E8] leading-relaxed max-w-lg mb-8 opacity-0"
          >
            You have held it together for so long, for so many people, in so many
            rooms, that somewhere along the way, you forgot what it felt like to
            just be yourself. You are not broken. You never were. You are simply
            ready to come home. This is where the coming home begins.
          </p>

          <div ref={buttonsRef} className="flex flex-col items-center sm:flex-row sm:items-start gap-3 sm:gap-4 mb-4 opacity-0">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-5 py-3 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap w-auto"
            >
  Book a Clarity Call
</a>
<p className="text-[#AAAAAA] text-xs font-body mt-2">
  <a href="https://paystack.com/buy/private-clarity-call-sdonrr" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#C9A84C]">Book a Private Clarity Call</a>
</p>
            <a
              href="#coaching-suite"
              className="inline-flex items-center justify-center px-5 py-3 border border-[#C9A84C] text-[#C9A84C] text-sm font-body font-medium rounded-full hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 whitespace-nowrap w-auto"
            >
              Explore the Work
            </a>
          </div>

          <p
            ref={smallTextRef}
            className="text-[#AAAAAA] text-xs font-body opacity-0"
          >
          </p>
        </div>

        <div className="hidden lg:block lg:w-[55%]" />
      </div>
    </section>
  );
}